import createObjectUrlForFileBlob from '../actions/createObjectUrlForFileBlob';
import type InlineImageViewState from '../schema/InlineImageViewState';
import createLogger from '../utils/logger';
import { EditorViewState, lazyOperateContent } from 'owa-editor';
import {
    AttachmentFileList,
    AttachmentFileType,
    LocalComputerFile,
} from 'owa-attachment-file-types';
import type { IEditor } from 'roosterjs-editor-types';
import { CID_PREFIX, ORIGINAL_SRC_ATTRIBUTE } from './constants';
import { AttachmentModel, isSmimeAttachment } from 'owa-attachment-model-store';
import { getAttachmentUrlForClassic } from 'owa-attachment-url';
import AttachmentUrlType from 'owa-files-url/lib/schema/AttachmentUrlType';
import getDataUrlFromFile from './getDataUrlFromFile';

export function setImageFromAttachment(img: HTMLImageElement, attachment: AttachmentModel) {
    const model = attachment.model;
    const cid = CID_PREFIX + model.ContentId;
    img.setAttribute(ORIGINAL_SRC_ATTRIBUTE, cid);
    img.setAttribute('size', model.Size?.toString() || '');
    img.setAttribute('contentType', model.ContentType || '');

    // For SMIME attachment, need to replace the SRC attribute with CID so that
    // it can be correctly encoded.
    // For regular attachment, replace with its attachment download url so that local lie can
    // still show it after compose is disposed.
    img.setAttribute(
        'src',
        isSmimeAttachment(attachment.id.Id)
            ? cid
            : getAttachmentUrlForClassic(attachment.id, AttachmentUrlType.Preview)
    );
}

export function createAndSetImageInEditor(
    editor: IEditor,
    imageSrc: string,
    attachment?: AttachmentModel
) {
    const img = editor.getDocument().createElement('img');
    img.style.maxWidth = '100%';
    img.src = imageSrc;

    if (attachment) {
        setImageFromAttachment(img, attachment);
    }
    editor.insertNode(img);
}

export default async function insertInlineImages(
    files: AttachmentFileList,
    editorViewState: EditorViewState,
    inlineImageViewState: InlineImageViewState
) {
    const localFiles: LocalComputerFile[] = [];
    const promises: Promise<string>[] = [];
    const logger = createLogger(editorViewState.editorId);

    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.fileType == AttachmentFileType.Local) {
            const localFile = <LocalComputerFile>file;
            localFiles.push(localFile);
            promises.push(getDataUrlFromFile(localFile.fileObject));
        }
    }

    if (localFiles.length > 0) {
        const dataUris = await Promise.all(promises);
        const operateContent = await lazyOperateContent.import();
        operateContent(editorViewState, editor => {
            editor.focus();
            editor.addUndoSnapshot(() => {
                localFiles.forEach((file, i) => {
                    const objectUrl = createObjectUrlForFileBlob(
                        inlineImageViewState,
                        file.fileObject,
                        dataUris[i]
                    );
                    createAndSetImageInEditor(editor, objectUrl);
                    file.tempId = objectUrl;
                    logger('newImage', 'insertImage');
                });
            }, 'InsertImage');

            return null;
        });
    }

    return files;
}
